import React, { useContext } from "react";
import styled from "styled-components";
import { Link, Icon, StatusIcon } from "components/common";
import Routes from "routing/Routes";
import { Input } from "components/bootstrap";
import FormDataContext from "integrations/contexts/FormDataContext";
import { ApiContext } from "integrations/aws/context/Api";
import useFetch from "integrations/hooks/useFetch";
import FormWrap from "integrations/aws/common/FormWrap";
import { ApiRoutes } from "integrations/aws/common/Routes";
import { DEFAULT_KINESIS_LOG_TYPE, KINESIS_LOG_TYPES } from "integrations/aws/common/constants";
import { toAWSRequest } from "integrations/aws/common/formDataAdapter";
import Store from "logic/local-storage/Store";
const Container = styled.div`
  border: 1px solid #a6afbd;
  margin: 25px 0;
  padding: 15px;
  border-radius: 4px;
`;
const Subheader = styled.h3`
  margin: 0 0 10px;
`;
const ReviewItems = styled.ul`
  list-style: none;
  margin: 0 0 25px 10px;
  padding: 0;

  li {
    padding: 5px;

    &:nth-of-type(odd) {
      background-color: rgb(220 225 229 / 40%);
    }
  }

  strong::after {
    content: ':';
    margin-right: 5px;
  }
`;
const EditAnchor = styled.a`
  font-size: 12px;
  margin-left: 5px;
  font-style: italic;
  cursor: pointer;

  &::before {
    content: '(';
  }

  &::after {
    content: ')';
  }
`;
const ArnErrorMessage = styled.span`
  color: #856404;
  background-color: #fff3cd;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
`;
const Default = ({ value }) => /* @__PURE__ */ React.createElement(React.Fragment, null, value, " ", /* @__PURE__ */ React.createElement("small", null, "(default)"));
const StepReview = ({ onSubmit, onEditClick, externalInputSubmit = false }) => {
  var _a, _b, _c;
  const { formData } = useContext(FormDataContext);
  const { logData } = useContext(ApiContext);
  const {
    awsAuthenticationType,
    awsCloudWatchAddFlowLogPrefix = { value: void 0 },
    awsAssumeRoleARN = { value: void 0 },
    awsAccessKey = { value: void 0 },
    awsCloudWatchAwsRegion,
    awsCloudWatchBatchSize,
    awsEndpointCloudWatch = { value: void 0 },
    awsCloudWatchKinesisInputType = { value: DEFAULT_KINESIS_LOG_TYPE },
    awsCloudWatchKinesisStream,
    awsCloudWatchName,
    awsCloudWatchThrottleEnabled = { value: void 0 },
    awsEndpointDynamoDB = { value: void 0 },
    awsEndpointIAM = { value: void 0 },
    awsEndpointKinesis = { value: void 0 },
    overrideSource = { value: void 0 }
  } = formData;
  const throttleEnabled = !!awsCloudWatchThrottleEnabled.value;
  const addPrefix = !!awsCloudWatchAddFlowLogPrefix.value;
  const awsCloudwatchKinesisStreamArn = (_b = (_a = formData.awsCloudwatchKinesisStreamArn) == null ? void 0 : _a.value) != null ? _b : "";
  const [fetchSubmitStatus, setSubmitFetch] = useFetch(
    null,
    (result) => {
      if (result == null ? void 0 : result.id) {
        Store.sessionSet("setup_wizard_input_id", result.id);
      }
      onSubmit();
    },
    "POST",
    toAWSRequest(formData, {
      name: awsCloudWatchName.value,
      region: awsCloudWatchAwsRegion.value,
      aws_input_type: awsCloudWatchKinesisInputType.value,
      stream_name: awsCloudWatchKinesisStream.value,
      batch_size: Number(awsCloudWatchBatchSize.value || awsCloudWatchBatchSize.defaultValue),
      enable_throttling: throttleEnabled,
      add_flow_log_prefix: addPrefix,
      kinesis_stream_arn: awsCloudwatchKinesisStreamArn,
      override_source: (_c = overrideSource == null ? void 0 : overrideSource.value) != null ? _c : ""
    })
  );
  const formError = fetchSubmitStatus.error ? {
    full_message: fetchSubmitStatus.error,
    nice_message: /* @__PURE__ */ React.createElement("span", null, "We were unable to save your Input, please try again in a few moments.")
  } : null;
  const handleSubmit = () => {
    if (externalInputSubmit) {
      onSubmit(formData);
      return;
    }
    setSubmitFetch(ApiRoutes.INTEGRATIONS.AWS.KINESIS.SAVE);
  };
  return /* @__PURE__ */ React.createElement(
    FormWrap,
    {
      onSubmit: handleSubmit,
      buttonContent: "Complete CloudWatch Setup",
      loading: fetchSubmitStatus.loading,
      error: formError,
      title: "Final Review",
      description: "Check out everything below to make sure it's correct, then click the button below to complete your CloudWatch setup!"
    },
    /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Subheader, null, "Setting up CloudWatch", " ", /* @__PURE__ */ React.createElement("small", null, /* @__PURE__ */ React.createElement(EditAnchor, { onClick: onEditClick("authorize") }, "Edit"))), /* @__PURE__ */ React.createElement(ReviewItems, null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Name"), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchName.value)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Authorization Type"), /* @__PURE__ */ React.createElement("span", null, awsAuthenticationType.value)), awsAccessKey.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "AWS Key"), /* @__PURE__ */ React.createElement("span", null, awsAccessKey.value)), awsAssumeRoleARN.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "AWS Assumed ARN Role"), /* @__PURE__ */ React.createElement("span", null, awsAssumeRoleARN.value)), awsEndpointCloudWatch.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "CloudWatch VPC Endpoint"), /* @__PURE__ */ React.createElement("span", null, awsEndpointCloudWatch.value)), awsEndpointDynamoDB.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "DynamoDB VPC Endpoint"), /* @__PURE__ */ React.createElement("span", null, awsEndpointDynamoDB.value)), awsEndpointIAM.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "IAM VPC Endpoint"), /* @__PURE__ */ React.createElement("span", null, awsEndpointIAM.value)), awsEndpointKinesis.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Kinesis VPC Endpoint"), /* @__PURE__ */ React.createElement("span", null, awsEndpointKinesis.value)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "AWS Region"), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchAwsRegion.value))), /* @__PURE__ */ React.createElement(Subheader, null, "Setting up Kinesis", " ", /* @__PURE__ */ React.createElement("small", null, /* @__PURE__ */ React.createElement(EditAnchor, { onClick: onEditClick("kinesis-setup") }, "Edit"))), /* @__PURE__ */ React.createElement(ReviewItems, null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Kinesis Stream"), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchKinesisStream.value)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Kinesis Stream ARN"), /* @__PURE__ */ React.createElement("span", null, !awsCloudwatchKinesisStreamArn ? /* @__PURE__ */ React.createElement(ArnErrorMessage, null, "Error: Failed to get stream ARN. Please ensure the IAM role includes the kinesis:DescribeStream permission.") : awsCloudwatchKinesisStreamArn)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Global Input"), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Icon, { name: "check" }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Record Batch Size"), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchBatchSize.value ? awsCloudWatchBatchSize.value : /* @__PURE__ */ React.createElement(Default, { value: awsCloudWatchBatchSize.defaultValue }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Enable Throttling"), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusIcon, { active: throttleEnabled }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Add Flow Log prefix to field names"), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusIcon, { active: addPrefix }))), overrideSource.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Override Source"), /* @__PURE__ */ React.createElement("span", null, overrideSource.value))), /* @__PURE__ */ React.createElement(Subheader, null, "Formatting"), /* @__PURE__ */ React.createElement(ReviewItems, null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, "Log Type"), /* @__PURE__ */ React.createElement("span", null, KINESIS_LOG_TYPES.find((type) => type.value === awsCloudWatchKinesisInputType.value).label))), /* @__PURE__ */ React.createElement("p", null, "If you need your logs parsed differently check out our", " ", /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.PIPELINES.RULES }, "Pipeline Rule"), " for additional details and instructions."), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "awsCloudWatchLog",
        type: "textarea",
        label: "",
        value: logData && logData.message || "We haven't received a response back from Amazon yet.",
        rows: 10,
        disabled: true
      }
    ))
  );
};
export default StepReview;
